/**
 * Composable para centralizar el funnel de PostHog: nombres de eventos
 * y propiedades comunes (hotel_id, room_id, pack, amount, currency, etc.).
 * Uso: const { trackSearchCompleted, trackHotelCardClicked, ... } = usePosthogFunnel()
 */

export const FunnelEvents = {
  SEARCH_COMPLETED: 'search_completed',
  HOTEL_CARD_CLICKED: 'hotel_card_clicked',
  HOTEL_VIEWED: 'hotel_viewed',
  ROOM_PACK_SELECTED: 'room_pack_selected',
  HOTEL_RESERVATION_CLICKED: 'hotel_reservation_clicked',
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_PAYMENT_SHOWN: 'checkout_payment_shown',
  CHECKOUT_COMPLETED: 'checkout_completed',
  COOKIE_CONSENT_ACCEPTED: 'cookie_consent_accepted',
  ALTERNATIVE_NEARBY_DATES_HOTEL_CLICKED: 'alternative_nearby_dates_hotel_clicked',
  ALTERNATIVE_NEARBY_HOTEL_CLICKED: 'alternative_nearby_hotel_clicked',
  ALTERNATIVE_MODULE_VIEWED: 'alternative_module_viewed',
  CHECKOUT_RECOVERY_HOTEL_LINK_CLICKED: 'checkout_recovery_hotel_link_clicked',
  CHECKOUT_RECOVERY_FINISH_CLICKED: 'checkout_recovery_finish_clicked',
  HOTELIERS_LEAD_FORM_SUBMITTED: 'hoteliers_lead_form_submitted',
  ON_DEMAND_FORM_SUBMITTED: 'on_demand_form_submitted',
  SEARCH_LOCATION_HISTORY_CLICKED: 'search_location_history_clicked',
  SEARCH_DATE_RANGE_MODE_CLICKED: 'search_date_range_mode_clicked',
  SEARCH_DATE_RANGE_HOTEL_CLICKED: 'search_date_range_hotel_clicked',
  STICKY_BOOKING_BAR_CLICKED: 'sticky_booking_bar_clicked'
} as const

export type FunnelEventName = (typeof FunnelEvents)[keyof typeof FunnelEvents]

export interface SearchCompletedProperties {
  destination_id?: number | null
  destination_name?: string
  date: string
  currency: string
  locale?: string
  results_count?: number
  has_destination: boolean
}

export interface HotelCardClickedProperties {
  hotel_id: number
  hotel_name?: string
  destination_id?: number | null
  destination_name?: string
  date?: string
  currency?: string
  min_price?: string | number
  is_mobile?: boolean
}

export interface HotelViewedProperties {
  hotel_id: number
  hotel_name?: string
  destination_id?: number | null
  destination_name?: string
  date?: string
  currency?: string
  rooms_count?: number
}

export interface RoomPackSelectedProperties {
  hotel_id: number
  hotel_name?: string
  room_id: number
  room_type?: string
  pack_hours: number
  checkin_hour?: string
  price?: string
  currency?: string
  date?: string
  is_mobile?: boolean
}

export interface HotelReservationClickedProperties {
  hotel_id: number
  hotel_name?: string
  room_id: number
  room_type?: string
  pack_hours: number
  checkin_hour?: string
  num_rooms?: number
  price?: string | number
  currency?: string
  date?: string
  extras_selected?: string
  is_mobile?: boolean
  button_location?: string
}

export interface CheckoutStartedProperties {
  hotel_id: number
  hotel_name?: string
  room_id: number
  pack_hours: number
  date?: string
  total_amount: number
  currency?: string
  num_rooms?: number
}

export interface CheckoutPaymentShownProperties {
  hotel_id: number
  hotel_name?: string
  room_id: number
  total_amount: number
  currency?: string
}

export interface CheckoutCompletedProperties {
  hotel_id?: number
  hotel_name?: string
  room_id?: number
  total_amount: number
  currency?: string
  date?: string
  pack_hours?: number
  reference?: string
}

export interface CookieConsentAcceptedProperties {
  button_id: string
  location: string
}

export interface AlternativeNearbyDatesHotelClickedProperties {
  hotel_id: number
  hotel_name?: string
  search_date: string
  available_date: string
  destination_id?: number | null
  currency?: string
  is_mobile?: boolean
}

export interface AlternativeNearbyHotelClickedProperties {
  clicked_hotel_id: number
  clicked_hotel_name?: string
  source_hotel_id: number
  source_hotel_name?: string
  date?: string
  currency?: string
  is_mobile?: boolean
}

export type AlternativeModuleType = 'nearby_dates' | 'alternatives' | 'nearby_hotels'

export interface AlternativeModuleViewedProperties {
  module_type: AlternativeModuleType
  items_count: number
  destination_id?: number | null
  destination_name?: string
  search_date?: string
  currency?: string
  locale?: string
  is_mobile?: boolean
}

export interface CheckoutRecoveryHotelLinkClickedProperties {
  hotel_id: number
  hotel_name?: string
  locale?: string
  is_mobile?: boolean
}

export interface CheckoutRecoveryFinishClickedProperties {
  hotel_id: number
  hotel_name?: string
  room_id?: number
  total_amount?: number
  currency?: string
  locale?: string
  is_mobile?: boolean
}

export interface HoteliersLeadFormSubmittedProperties {
  locale: string
  rooms: string
  has_website: boolean
  has_comments: boolean
  is_mobile?: boolean
}

export interface OnDemandFormSubmittedProperties {
  locale: string
  destination_id?: number | null
  checkin_date: string
  pack_hours: number
  has_attempted_booking_note: boolean
  is_mobile?: boolean
}

export interface SearchLocationHistoryClickedProperties {
  destination_id?: number | null
  destination_name?: string
  category_id?: number
  destination_type?: string
  country_id?: string
  locale?: string
  is_mobile?: boolean
  surface?: 'modal' | 'input_menu'
}

export type SearchDateRangeMode = 'plus1' | 'minus1' | 'both'

export interface SearchDateRangeModeClickedProperties {
  /** Botón pulsado: +1 día, -1 día o ±1 día */
  mode: SearchDateRangeMode
  action: 'selected' | 'deselected'
  previous_mode?: SearchDateRangeMode | ''
  new_mode?: SearchDateRangeMode | ''
  search_date?: string
  destination_id?: number | null
  locale?: string
  is_mobile?: boolean
  surface?: 'calendar_modal' | 'calendar_popover'
}

export interface SearchDateRangeHotelClickedProperties {
  hotel_id: number
  hotel_name?: string
  /** Fecha elegida en el buscador (día principal) */
  search_date: string
  /** Fecha en la que el hotel tiene disponibilidad (badge en tarjeta) */
  available_date: string
  date_range_mode?: SearchDateRangeMode | ''
  destination_id?: number | null
  destination_name?: string
  currency?: string
  min_price?: string | number
  is_mobile?: boolean
}

/** CTA principal de la barra sticky en ficha hotel (3 estados de conversión). */
export type StickyBookingBarCtaType = 'view_rooms' | 'choose_time' | 'reserve_now'

export type StickyBookingBarState =
  | 'empty_selection'
  | 'partial'
  | 'full'
  | 'no_rooms'
  | 'loading'

export interface StickyBookingBarClickedProperties {
  /** Agrupación funnel: ver habitaciones | elegir hora | reservar ahora */
  cta_type: StickyBookingBarCtaType
  bar_state: StickyBookingBarState
  cta_label: string
  hotel_id?: number
  hotel_name?: string
  room_id?: number
  room_name?: string
  pack_hours?: number
  checkin_hour?: string | null
  price?: string | number
  currency?: string
  date?: string
  is_mobile?: boolean
  has_alternative_dates?: boolean
  /** Solo cta_type reserve_now: checkout directo vs redirección al modal de extras */
  reserve_action?: 'checkout' | 'extras_modal'
}

function usePosthogClient() {
  const config = useRuntimeConfig()
  if (config.public.DISABLE_COOKIES || !config.public.POSTHOG_KEY?.trim()) {
    return undefined
  }
  if (!import.meta.client) {
    return undefined
  }
  const getClient = useNuxtApp().$posthog as (() => { capture?: (event: string, props?: Record<string, unknown>) => void } | undefined) | undefined
  return getClient?.()
}

export function usePosthogFunnel() {
  const posthog = usePosthogClient()

  function track(eventName: FunnelEventName | string, properties?: object) {
    if (import.meta.client && posthog?.capture) {
      posthog.capture(eventName, (properties ?? {}) as Record<string, unknown>)
    }
  }

  function trackSearchCompleted(properties: SearchCompletedProperties) {
    track(FunnelEvents.SEARCH_COMPLETED, properties)
  }

  function trackHotelCardClicked(properties: HotelCardClickedProperties) {
    track(FunnelEvents.HOTEL_CARD_CLICKED, properties)
  }

  function trackHotelViewed(properties: HotelViewedProperties) {
    track(FunnelEvents.HOTEL_VIEWED, properties)
  }

  function trackRoomPackSelected(properties: RoomPackSelectedProperties) {
    track(FunnelEvents.ROOM_PACK_SELECTED, properties)
  }

  function trackHotelReservationClicked(properties: HotelReservationClickedProperties) {
    track(FunnelEvents.HOTEL_RESERVATION_CLICKED, properties)
  }

  function trackCheckoutStarted(properties: CheckoutStartedProperties) {
    track(FunnelEvents.CHECKOUT_STARTED, properties)
  }

  function trackCheckoutPaymentShown(properties: CheckoutPaymentShownProperties) {
    track(FunnelEvents.CHECKOUT_PAYMENT_SHOWN, properties)
  }

  function trackCheckoutCompleted(properties: CheckoutCompletedProperties) {
    track(FunnelEvents.CHECKOUT_COMPLETED, properties)
  }

  function trackCookieConsentAccepted(properties: CookieConsentAcceptedProperties) {
    track(FunnelEvents.COOKIE_CONSENT_ACCEPTED, properties)
  }

  function trackAlternativeNearbyDatesHotelClicked(properties: AlternativeNearbyDatesHotelClickedProperties) {
    track(FunnelEvents.ALTERNATIVE_NEARBY_DATES_HOTEL_CLICKED, properties)
  }

  function trackAlternativeNearbyHotelClicked(properties: AlternativeNearbyHotelClickedProperties) {
    track(FunnelEvents.ALTERNATIVE_NEARBY_HOTEL_CLICKED, properties)
  }

  function trackAlternativeModuleViewed(properties: AlternativeModuleViewedProperties) {
    track(FunnelEvents.ALTERNATIVE_MODULE_VIEWED, properties)
  }

  function trackCheckoutRecoveryHotelLinkClicked(properties: CheckoutRecoveryHotelLinkClickedProperties) {
    track(FunnelEvents.CHECKOUT_RECOVERY_HOTEL_LINK_CLICKED, properties)
  }

  function trackCheckoutRecoveryFinishClicked(properties: CheckoutRecoveryFinishClickedProperties) {
    track(FunnelEvents.CHECKOUT_RECOVERY_FINISH_CLICKED, properties)
  }

  function trackHoteliersLeadFormSubmitted(properties: HoteliersLeadFormSubmittedProperties) {
    track(FunnelEvents.HOTELIERS_LEAD_FORM_SUBMITTED, properties)
  }

  function trackOnDemandFormSubmitted(properties: OnDemandFormSubmittedProperties) {
    track(FunnelEvents.ON_DEMAND_FORM_SUBMITTED, properties)
  }

  function trackSearchLocationHistoryClicked(properties: SearchLocationHistoryClickedProperties) {
    track(FunnelEvents.SEARCH_LOCATION_HISTORY_CLICKED, properties)
  }

  function trackSearchDateRangeModeClicked(properties: SearchDateRangeModeClickedProperties) {
    track(FunnelEvents.SEARCH_DATE_RANGE_MODE_CLICKED, properties)
  }

  function trackSearchDateRangeHotelClicked(properties: SearchDateRangeHotelClickedProperties) {
    track(FunnelEvents.SEARCH_DATE_RANGE_HOTEL_CLICKED, properties)
  }

  function trackStickyBookingBarClicked(properties: StickyBookingBarClickedProperties) {
    track(FunnelEvents.STICKY_BOOKING_BAR_CLICKED, properties)
  }

  return {
    track,
    trackSearchCompleted,
    trackHotelCardClicked,
    trackHotelViewed,
    trackRoomPackSelected,
    trackHotelReservationClicked,
    trackCheckoutStarted,
    trackCheckoutPaymentShown,
    trackCheckoutCompleted,
    trackCookieConsentAccepted,
    trackAlternativeNearbyDatesHotelClicked,
    trackAlternativeNearbyHotelClicked,
    trackAlternativeModuleViewed,
    trackCheckoutRecoveryHotelLinkClicked,
    trackCheckoutRecoveryFinishClicked,
    trackHoteliersLeadFormSubmitted,
    trackOnDemandFormSubmitted,
    trackSearchLocationHistoryClicked,
    trackSearchDateRangeModeClicked,
    trackSearchDateRangeHotelClicked,
    trackStickyBookingBarClicked,
    FunnelEvents
  }
}
